import { Circle } from 'ol/style';
import RegularShape from 'ol/style/RegularShape';
import { measureTextHeight, measureTextWidth } from 'ol/render/canvas';
import { apply, compose, create } from 'ol/transform';
import { boundingExtent } from 'ol/extent';

import { convertMmToPoints } from 'cadenza/map/unit-utils';
import { assertNonNullable, assertType } from 'cadenza/utils/custom-error';
import { array } from 'cadenza/utils/array-util';

import type { FeatureLike } from 'ol/Feature';
import type { Style } from 'ol/style';
import type { Feature } from 'ol';
import type { StyleLike } from 'ol/style/Style';

// For whatever reason OL5 decides not to clone some properties
export function cloneStyle (style: StyleLike) {
  if (typeof style === 'function') {
    return style;
  } else if (Array.isArray(style)) {
    return style.map(cloneStyleObject);
  }
  return cloneStyleObject(style);
}

function cloneStyleObject (style: Style) {
  const clonedStyle = style.clone();

  const clonedTextStyle = clonedStyle.getText();
  if (clonedTextStyle) {
    const originalTextPadding = style.getText()!.getPadding();
    if (originalTextPadding) {
      clonedTextStyle.setPadding(originalTextPadding.slice());
    }
  }

  clonedStyle.setRenderer(style.getRenderer());
  return clonedStyle;
}

/**
 * Merges the 2 styles returning a StyleFunction if 1 of the inputs is a style function,
 * otherwise returns an array of styles obtained by concatenating the 2 input styles.
 *
 * @param style1 - The first style to merge
 * @param style2 - The second style to merge. Note that this is appended to the first
 */
export function mergeStyles (style1: StyleLike, style2: StyleLike): StyleLike {
  if (typeof style1 === 'function' || typeof style2 === 'function') {
    return (feature, resolution) => {
      return [ ...resolveStyle(style1, feature, resolution), ...resolveStyle(style2, feature, resolution) ];
    };
  }
  return [ ...resolveStyle(style1), ...resolveStyle(style2) ];
}

function resolveStyle (style1: StyleLike, feature?: FeatureLike, resolution?: number) {
  if (typeof style1 === 'function') {
    assertNonNullable(feature, 'The feature is a mandatory parameter to a style function');
    assertNonNullable(resolution, 'The resolution is a mandatory parameter to a style function');
    const resolvedStyle = style1(feature, resolution);
    return resolvedStyle != null ? array(resolvedStyle) : [];
  }
  return array(style1);
}

// "renderProperties": RenderProperties, is not a supported api property of Style, it is set here in select-styles.js
interface RenderProperties {
  width: number;
  height: number;
  rotation: number;
}

/**
 * Calculates the buffer in pixels for a given style as calculated by select-styles/createStyles().
 * When this buffer is applied to an extent the resulting extent contains the whole feature as drawn.
 * This is mostly useful for point features.
 *
 * @param style - the style
 * @param groupFeature - the feature for which style is the selection style
 * @return - either a single number or an array with offsets for left, bottom, right, top
 */
export function pixelBuffer (style: Style & { renderProperties?: RenderProperties }, groupFeature: Feature) {
  const image = style.getImage();
  const textStyle = style.getText();
  const renderProperties = style.renderProperties;
  if (textStyle) {
    const font = textStyle.getFont();
    const text = textStyle.getText();
    assertNonNullable(font);
    assertType<string>(text, typeof text === 'string' || text instanceof String);
    const width = measureTextWidth(font, text);
    const height = measureTextHeight(font);
    const [ top, right, bot, left ] = textStyle.getPadding() ?? [ 0, 0, 0, 0 ];
    const hackyLineWidthAdjustment = 2;
    // bottom-left x,y upper-right x,y
    const anchorUpperLeft = [
      -left,
      -(height + bot),
      width + right + hackyLineWidthAdjustment,
      top
    ];
    const textRotation = textStyle.getRotation();
    if (textStyle.getTextAlign() === 'left' && textStyle.getTextBaseline() === 'top' && textRotation === 0) {
      return anchorUpperLeft;
    }
    // we need to translate and rotate
    let dx = 0;
    let dy = 0;
    if (textStyle.getTextAlign() === 'center') {
      dx = -(width / 2);
    } else if (textStyle.getTextAlign() === 'right') {
      dx = -width;
    }
    if (textStyle.getTextBaseline() === 'alphabetic') {
      dy = (height / 2) + hackyLineWidthAdjustment;
    } else if (textStyle.getTextBaseline() === 'bottom') {
      dy = height;
    }
    // compose and textStyle.getRotation() define rotation in cw vs. ccw so we negate here
    return calcTranslatedRotatedSelectionBox(dx, dy, textRotation != null ? -textRotation : 0, anchorUpperLeft);
  }
  if (renderProperties) {
    // the case for IconFrameStyle
    const halfWidth = Math.round(renderProperties.width / 2);
    const halfHeight = Math.round(renderProperties.height / 2);
    const anchor = [
      -halfWidth,
      -halfHeight,
      halfWidth,
      halfHeight
    ];
    if (renderProperties.rotation === 0) {
      return anchor;
    }
    return calcTranslatedRotatedSelectionBox(0, 0, -renderProperties.rotation, anchor);
  }
  if (image) {
    if (image instanceof Circle || image instanceof RegularShape) {
      return image.getRadius();
    }
  }
  const groupStyle = groupFeature.getStyle();
  let width;
  if (groupStyle && !Array.isArray(groupStyle) && typeof groupStyle !== 'function') {
    width = groupStyle.getStroke()?.getWidth();
  }
  if (width) {
    // case for polys, rects, ovals and lines with stroke width
    return Math.ceil(width / 2);
  }
  return 0;
}

function calcTranslatedRotatedSelectionBox (dx: number, dy: number, rotation: number, box: number[]) {
  const transform = create();
  compose(transform, 0, 0, 1, 1, rotation, dx, dy);
  const bottomLeft = [ box[0], box[1] ];
  const upperLeft = [ box[0], box[3] ];
  const upperRight = [ box[2], box[3] ];
  const bottomRight = [ box[2], box[1] ];
  const points = [ bottomLeft, upperLeft, upperRight, bottomRight ];
  for (const point of points) {
    apply(transform, point);
  }
  return boundingExtent(points);
}

/**
 * Calculates the azimuthal rotation between 2 coordinates.
 *
 * @param firstCoordinate - The first coordinate
 * @param secondCoordinate - The second coordinate
 * @return the azimuthal rotation in degrees.
 */
export function calculateRotation (firstCoordinate: number[], secondCoordinate: number[]) {
  const dx = secondCoordinate[0] - firstCoordinate[0];
  const dy = secondCoordinate[1] - firstCoordinate[1];
  const rawAngle = Math.atan2(dx, dy);

  // Round to 1/10 steps in case this is used as a cache map key, if it's too precise, it would be useless as a key in a cache map.
  return Math.round(rawAngle * 10) / 10;
}

export function getSize (size: number, unit: string) {
  if (unit === 'mm') {
    return convertMmToPoints(size);
  }
  return Number(size);
}
